import { Component, OnInit, OnDestroy, Input, ChangeDetectionStrategy } from '@angular/core';
import { ProvisionHeader } from '../../models/provisionheader';
import { ProvisionItem } from '../../models/provisionitem';
import { Provision } from '../../models/provision';
import { SearchProvisionItemsPipe } from '../../pipes/searchprovisionitems/searchprovisionitems.pipe';
import { SearchProvisionHeader } from '../../pipes/searchprovision/searchprovision.pipe';
import { Subscription } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { StructureService } from "../../services/structure/structure.service";
import { ProvisionService } from 'src/services/provision/provision.service';
import { ProvisionChange } from 'src/models/provisionchange';
import { ProductType } from 'src/models/producttype';
import { ProvisionContentRow } from 'src/models/provisioncontent';
import { UserService } from 'src/services/user/user.service';
import { StateService } from 'src/services/state/state.service';
import { apiErrorMessage } from 'src/services/baseapi/api-error-message';


@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
    selector: 'conversion-provision',
    templateUrl: './provision.component.html',
    styleUrls: ['./provision.component.css']
})

export class ProvisionComponent implements OnInit, OnDestroy {
    @Input() id: string;

    constructor(public provisionService: ProvisionService,
        private userService: UserService,
        private structureService: StructureService,
        private stateService: StateService
    ) { }

    provision = new Provision();
    provisionPipeResult = new Array<ProvisionItem>();
    searchProvisionPipe = new SearchProvisionItemsPipe();
    userProvisions = new Array<ProvisionHeader>();
    subscriptions: Subscription[] = [];
    provisionName: string;
    provisionUserName: string;
    changes: Array<ProvisionChange> = new Array<ProvisionChange>();
    noProductType: boolean;
    provisionLoadFailed: boolean;
    provisionSaveFailed = false;
    provisionDetailsAvailable = false;
    userProvisionsLoading = false;
    userProvisionsLoadFailed = false;
    provisionListOpen = false;
    activeProvisionIndex = -1;
    provisionLoading = false;
    provisionSaving = false;
    provisionSaved = false;
    provisionLoadMessage = '';
    provisionSaveMessage = '';
    userProvisionsMessage = '';
    private savedProvisionNeedsReload = false;
    private activeProvisionOwner: string;
    private activeLoad: Subscription;
    private activeList: Subscription;
    private activeSave: Subscription;
    private loadedProvisionUserName: string;
    private readonly provisionNameFilter = new SearchProvisionHeader();
    lastSaved: Date | null = null;
    partPeriodInput = '';

    setPartPeriod(value: string) {
        this.partPeriodInput = value;
        this.provisionService.insertPDVersion(value);
    }
    get invalidPartPeriod(): boolean {
        return !!this.provision.Name && !/^[0-9]{4}\.?(?:0[1-9]|1[0-2])\.?[1-4]$/.test(this.partPeriodInput);
    }


    get provisionLibraryMessage(): string {
        return (this.provisionUserName || '').trim() ? ''
            : 'Enter a provision library to continue. If you do not know your library name, contact support.';
    }

    get canSave(): boolean {
        return !this.provisionSaving && !this.provisionLoading && !this.savedProvisionNeedsReload &&
            (!this.activeProvisionOwner || this.activeProvisionOwner === this.provisionUserName) &&
            this.provisionName === this.provision.Name &&
            this.provisionService.canSaveProvision();
    }

    get showSaveConfirmation(): boolean {
        return this.provisionSaved && !this.savedProvisionNeedsReload && !this.provisionService.hasPendingChanges();
    }

    get filteredUserProvisions(): ProvisionHeader[] {
        try {
            return this.provisionNameFilter.transform(this.userProvisions, this.provisionName);
        } catch {
            return this.userProvisions;
        }
    }

    ngOnInit(): void {
        this.subscriptions.push(this.userService.getUserUpdates()
            .subscribe(user => {
                if (user.id) {
                    this.provisionUserName = user.provisionsUserName;
                    this.fillUserProvisions()
                }
            }))

        this.subscriptions.push(this.provisionService.getProvisionUpdates()
            .subscribe(provision => {
                if (this.provision !== provision) { this.partPeriodInput = provision.PDVersion || ''; }
                this.provision = provision;
            }))
    }

    fillUserProvisions() {
        this.activeLoad?.unsubscribe();
        this.activeList?.unsubscribe();
        //Empty the current user provisions and clear the service first
        this.userService.setProvisionsUserName(this.provisionUserName);
        this.userProvisions = new Array<ProvisionHeader>();
        this.userProvisionsLoadFailed = false;
        this.userProvisionsMessage = '';
        this.loadedProvisionUserName = this.provisionUserName;
        if (this.provisionUserName) {
            this.getUserProvisions();
        }
        this.provisionName = '';
        this.closeProvisionList();
    }

    onProvisionLibraryCommit() {
        const library = (this.provisionUserName || '').trim();
        if (library !== this.provisionUserName) {
            this.provisionUserName = library;
        }
        if (library !== (this.loadedProvisionUserName || '')) {
            this.fillUserProvisions();
        }
    }

    openProvisionList() {
        if (this.userProvisions.length > 0 || this.userProvisionsLoading) {
            this.provisionListOpen = true;
        }
    }

    closeProvisionList() {
        this.provisionListOpen = false;
        this.activeProvisionIndex = -1;
    }

    toggleProvisionList() {
        if (this.provisionListOpen) {
            this.closeProvisionList();
        } else {
            this.openProvisionList();
        }
    }

    onProvisionNameInput() {
        this.activeProvisionIndex = -1;
        this.openProvisionList();
    }

    onProvisionNameKeydown(event: KeyboardEvent) {
        const options = this.filteredUserProvisions;
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                this.openProvisionList();
                if (options.length > 0) {
                    this.activeProvisionIndex = (this.activeProvisionIndex + 1) % options.length;
                }
                break;
            case 'ArrowUp':
                event.preventDefault();
                this.openProvisionList();
                if (options.length > 0) {
                    this.activeProvisionIndex = this.activeProvisionIndex <= 0 ? options.length - 1 : this.activeProvisionIndex - 1;
                }
                break;
            case 'Enter': {
                const name = (this.provisionName || '').toLowerCase();
                const match = options[this.activeProvisionIndex]
                    || options.find(x => x.ProvisionName.toLowerCase() === name)
                    || (options.length === 1 ? options[0] : undefined);
                if (match) {
                    event.preventDefault();
                    this.selectProvision(match);
                }
                break;
            }
            case 'Escape':
                if (this.provisionListOpen) {
                    event.preventDefault();
                    this.closeProvisionList();
                }
                break;
        }
    }

    getProvision(afterSave = false) {
        this.activeLoad?.unsubscribe();
        const owner = afterSave ? this.activeProvisionOwner : this.provisionUserName;
        const name = afterSave ? this.provision.Name : this.provisionName;
        if (!afterSave) {
            this.provisionService.clearData();
            this.provision = new Provision();
            this.activeProvisionOwner = undefined;
        }
        this.savedProvisionNeedsReload = afterSave;
        this.provisionSaved = afterSave;
        this.noProductType = false;
        this.provisionLoadFailed = false;
        this.provisionSaveFailed = false;
        this.provisionLoadMessage = '';
        this.provisionSaveMessage = '';
        this.provisionDetailsAvailable = false;
        this.provisionLoading = true;
        this.stateService.insertProvisionLoading(true);

        this.activeLoad = this.structureService.getProvision(name, owner).pipe(
            map(content => ({ content, provision: Provision.fromContent(name, content) })),
            finalize(() => {
                this.provisionLoading = false;
                this.stateService.insertProvisionLoading(false);
            })
        ).subscribe(
            ({ content, provision }) => {
                if (afterSave) { this.provisionService.clearData(); }
                this.provisionService.insertProvisionContent(content);
                this.provisionDetailsAvailable = !!content.Details;
                this.provision = provision;
                this.partPeriodInput = provision.PDVersion || '';
                this.provisionName = provision.Name;
                this.activeProvisionOwner = owner;
                this.savedProvisionNeedsReload = false;
                const productClass = provision.Provision.find(item => item.Family === '1');
                if (productClass) {
                    this.provisionService.insertProductType(new ProductType(productClass.Execution, productClass.ExecutionDescription));
                } else {
                    this.noProductType = true;
                }
                this.provisionService.insertProvision(provision);
                if (afterSave) {
                    this.lastSaved = new Date();
                }
            }, error => {
                this.provisionLoadFailed = true;
                this.provisionLoadMessage = afterSave
                    ? 'Your changes were saved, but we could not refresh the provision. Try loading it again before making more changes.'
                    : apiErrorMessage(error, 'load');
                console.error(error);
            }
        )
    }

    selectProvision(provisionHeader: ProvisionHeader) {
        this.closeProvisionList();
        this.loadSelection(provisionHeader);
    }

    private loadSelection(provisionHeader: ProvisionHeader) {
        this.lastSaved = null;
        this.provisionUserName = provisionHeader.ProvisionLibrary;
        this.provisionName = provisionHeader.ProvisionName;
        this.getProvision();
    }

    saveProvision() {
        if (this.provisionSaving || this.provisionLoading || this.savedProvisionNeedsReload) {
            return;
        }
        const content = this.provisionService.getProvisionContent();
        if (!content) {
            this.provisionSaveFailed = true;
            this.provisionSaveMessage = 'Choose a provision before saving.';
            return;
        }
        if (this.activeProvisionOwner && (this.activeProvisionOwner !== this.provisionUserName || this.provisionName !== this.provision.Name)) {
            this.provisionSaveFailed = true;
            this.provisionSaveMessage = 'Choose a provision from the selected library before saving.';
            return;
        }
        this.provisionSaveFailed = false;
        this.provisionSaveMessage = '';
        this.provisionSaved = false;
        let partPeriod: string | undefined;
        let rows: ProvisionContentRow[];
        try {
            partPeriod = this.provisionService.getPartPeriodForSave();
            rows = this.provisionService.getRowsForSave();
        } catch {
            this.provisionSaveFailed = true;
            this.provisionSaveMessage = 'Check your changes and enter a valid part period, for example 2024.01.1, before saving.';
            return;
        }
        if (!this.provisionService.canSaveProvision()) {
            this.provisionSaveFailed = true;
            this.provisionSaveMessage = 'Analyze the current changes successfully before saving.';
            return;
        }
        this.provisionSaving = true;
        this.stateService.insertProvisionSaving(true);
        this.activeSave = this.structureService.saveProvision(this.provision.Name, this.activeProvisionOwner || this.provisionUserName,
            content.EntityTag, rows, partPeriod).pipe(
                finalize(() => {
                    this.provisionSaving = false;
                    this.stateService.insertProvisionSaving(false);
                })
            ).subscribe(
            () => {
                this.provisionSaved = true;
                this.savedProvisionNeedsReload = true;
                this.getProvision(true);
            },
            error => {
                this.provisionSaveFailed = true;
                this.provisionSaveMessage = apiErrorMessage(error, 'save');
                console.error(error);
            }
        );
    }

    getUserProvisions() {
        this.activeList?.unsubscribe();
        if (this.provisionLibraryMessage) {
            this.userProvisions = [];
            this.userProvisionsLoadFailed = false;
            this.userProvisionsMessage = '';
            return;
        }
        this.userProvisionsLoading = true;
        this.userProvisionsLoadFailed = false;
        this.userProvisionsMessage = '';
        this.activeList = this.structureService.getProvisionsForUser(this.provisionUserName).pipe(
            map(data => {
                if (!Array.isArray(data) || data.some(item =>
                    typeof item?.ProvisionOwner !== 'string' || typeof item?.ProvisionName !== 'string')) {
                    throw new Error('Invalid provision list');
                }
                return data.map(item => ProvisionHeader.fromApi(item));
            }),
            finalize(() => this.userProvisionsLoading = false)
        ).subscribe(
            data => {
                this.userProvisions = data;
            }, error => {
                this.userProvisionsLoadFailed = true;
                this.userProvisionsMessage = apiErrorMessage(error, 'list');
                console.log(error);
            }
        )
    }

    ngOnDestroy() {
        this.activeLoad?.unsubscribe();
        this.activeList?.unsubscribe();
        this.activeSave?.unsubscribe();
        this.subscriptions.forEach(s => s.unsubscribe());
    }
}
